import React, { useMemo, useState } from 'react';

import {
  Active,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { List } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'state/hooks';
import { setRmHovered } from 'state/routeMonitoringSlice';
import {
  SavedRouteAndFeature,
  selectCurrentSavedRoutes,
  selectStatsSavedRoutes,
  setCurrentSavedRoutes,
  useSavedRouteUpdateMutation,
} from 'state/savedRouteSlices';
import {
  selectProject,
  selectRouteSelection,
  setRouteSelection,
} from 'state/workflowSlice';

import { TArrayFilters } from 'appTypes';
import { SortableOverlay } from 'features/common/Sortable/SortableOverlay';

import { useFilteredArrayOfObjects } from '../common/hooks/useFilteredArrayOfObjects';
import { RouteRow } from './RouteRow';

import styles from './RouteMonitoringList.module.css';

interface SortableMonitorListInnerProps {
  filters: TArrayFilters;
  sort?: string;
  sortOrder?: number;
}

export function SortableMonitorListInner({
  filters,
  sort,
  sortOrder,
}: SortableMonitorListInnerProps) {
  const dispatch = useAppDispatch();
  const [activeRow, setActiveRow] = useState<Active | null>(null);
  const project_slug = useAppSelector(selectProject);
  const routes_all = useAppSelector(selectCurrentSavedRoutes);
  const routeSelection = useAppSelector(selectRouteSelection);
  const stats = useAppSelector(selectStatsSavedRoutes);

  const [updateSavedRoute] = useSavedRouteUpdateMutation();
  const routes = useFilteredArrayOfObjects(
    routes_all,
    'savedRoute',
    filters || {},
    sort,
    sortOrder,
    stats,
  );

  const activeItem = useMemo(
    () => routes.find((route) => route.id === activeRow?.id),
    [activeRow, routes],
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onHover = (route: string) => {
    dispatch(setRmHovered(route));
  };

  const onSelect = (savedRouteId, onMount = false) => {
    const isOpen = routeSelection === savedRouteId;
    if (isOpen) {
      dispatch(setRouteSelection(undefined));
    } else if (!onMount) {
      dispatch(setRouteSelection(savedRouteId));
    }
  };

  const disableOrdering = Boolean(sortOrder) || Boolean(filters.routeName);

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) setActiveRow(null);
    else {
      const activeIndex = routes.findIndex(({ id }) => id === active.id);
      const route = routes[activeIndex];
      const overIndex = routes.findIndex(({ id }) => id === over.id);
      const activeId = routes[activeIndex].id;
      const overId = routes[overIndex].id;
      const oldIndex = routes_all.findIndex((el) => el.id === activeId);
      const newIndex = routes_all.findIndex((el) => el.id === overId);
      const newRoutes = arrayMove(
        routes_all,
        oldIndex,
        newIndex,
      ) as SavedRouteAndFeature[];
      const before_id = newRoutes[newIndex - 1]?.savedRoute.id ?? undefined;
      const after_id = newRoutes[newIndex + 1]?.savedRoute.id ?? undefined;
      dispatch(setCurrentSavedRoutes(newRoutes));
      updateSavedRoute({
        project_slug: project_slug as string,
        savedRouteId: route.id,
        savedRoute: {
          routeName: route.savedRoute.routeName,
          routeSegmentIds: route.savedRoute.routeSegmentIds,
          realizedSegments: route.savedRoute.realizedSegments,
          starred: route.savedRoute.starred,
          before_id,
          after_id,
        },
      });
      setActiveRow(null);
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={({ active }) => {
        setActiveRow(active);
      }}
      onDragEnd={handleDragEnd}
      onDragCancel={() => {
        setActiveRow(null);
      }}
    >
      <SortableContext items={routes}>
        <List className={styles.custom_list}>
          {routes &&
            routes.map((route, i) => (
              <RouteRow
                disableOrdering={disableOrdering}
                onSelect={onSelect}
                key={route.savedRoute.id}
                route={route}
                onHover={onHover}
                isOpen={routeSelection === route.savedRoute.id && i !== -1}
              />
            ))}
        </List>
      </SortableContext>
      <SortableOverlay>
        {activeItem ? (
          <RouteRow
            disableOrdering={disableOrdering}
            onSelect={onSelect}
            key={activeItem.savedRoute.id}
            route={activeItem}
            onHover={onHover}
            isOpen={false}
          />
        ) : null}
      </SortableOverlay>
    </DndContext>
  );
}
